import React, { useState, useRef, useEffect, useContext } from 'react';
import { cn } from '@/shared/utils/cn';

interface DropdownMenuProps {
  children: React.ReactNode;
  className?: string;
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

interface DropdownMenuContentProps {
  children: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  className?: string;
}

interface DropdownMenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  /**
   * Alias estilo Radix. LeadBulkToolbar (y futuros ports de shadcn) pasan
   * onSelect — sin este alias el item renderiza pero el click NO HACE NADA
   * (bug silencioso detectado 2026-06-11).
   */
  onSelect?: () => void;
  disabled?: boolean;
  className?: string;
}

interface DropdownMenuSeparatorProps {
  className?: string;
}

// Context for dropdown state
const DropdownContext = React.createContext<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}>({
  isOpen: false,
  setIsOpen: () => {},
});

const DropdownMenu: React.FC<DropdownMenuProps> = ({ children, className }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownContext.Provider value={{ isOpen, setIsOpen }}>
      <div className={cn('relative', className)}>{children}</div>
    </DropdownContext.Provider>
  );
};

const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({
  children,
  asChild = false,
  className,
}) => {
  const { isOpen, setIsOpen } = useContext(DropdownContext);

  // Bug fix 2026-06-11 (TINA): el toggle ahora frena la propagacion SIEMPRE.
  // Antes, en modo asChild, `onClick: handleClick` PISABA el onClick del
  // child (tipicamente `e.stopPropagation()` puesto por el consumer) → el
  // click burbujeaba al card/row clickeable de atras → su accion (abrir
  // modal, navegar) disparaba ENCIMA del menu recien abierto. Sintoma
  // reportado: "los menus ⋮ no funcionan" en kanban CRM, tablas, etc.
  const handleClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsOpen(!isOpen);
  };

  if (asChild && React.isValidElement(children)) {
    const childOnClick = (children.props as { onClick?: (e: React.MouseEvent) => void }).onClick;
    return React.cloneElement(children as React.ReactElement, {
      onClick: (e: React.MouseEvent) => {
        childOnClick?.(e); // preserva el handler del consumer (no lo pisa)
        handleClick(e);
      },
      className: cn((children.props as { className?: string }).className, className),
    });
  }

  return (
    <button
      onClick={handleClick}
      className={cn('outline-none', className)}
      aria-expanded={isOpen}
      aria-haspopup="true"
    >
      {children}
    </button>
  );
};

const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({
  children,
  align = 'start',
  className,
}) => {
  const { isOpen, setIsOpen } = useContext(DropdownContext);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  if (!isOpen) return null;

  const alignmentClasses = {
    start: 'left-0',
    center: 'left-1/2 transform -translate-x-1/2',
    end: 'right-0',
  };

  return (
    <div
      ref={contentRef}
      className={cn(
        'absolute top-full mt-1 z-50 min-w-[8rem] overflow-hidden rounded-md border bg-white shadow-lg',
        alignmentClasses[align],
        className
      )}
    >
      <div className="p-1">{children}</div>
    </div>
  );
};

const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({
  children,
  onClick,
  onSelect,
  disabled = false,
  className,
}) => {
  const { setIsOpen } = useContext(DropdownContext);

  // Mismo fix que el trigger: sin stopPropagation, clickear "Editar" en el
  // menu de un card clickeable disparaba TAMBIEN el onClick del card.
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const action = onClick ?? onSelect;
    if (!disabled && action) {
      action();
      setIsOpen(false);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
        'hover:bg-gray-100 focus:bg-gray-100',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {children}
    </div>
  );
};

const DropdownMenuSeparator: React.FC<DropdownMenuSeparatorProps> = ({
  className,
}) => {
  return <div className={cn('my-1 h-px bg-gray-200', className)} />;
};

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
export default DropdownMenu;
